import {
  CheckIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PlusIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import {
  Avatar,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Textarea,
  Tooltip,
} from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";

import { getFighters, shareEvent, uploadMediaFile } from "../../services/api";
import { ShareEventFileRequest, ShareEventRequest } from "../../types/types";

type ShareTab = "fighters" | "email";

interface ShareFighter {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  profileImage?: string | null;
  verified?: boolean | null;
  location?: string | null;
  city?: string | null;
  country?: string | null;
  trainingDiscipline?: string | null;
  weightClass?: string | null;
}

interface EventShareProps {
  eventId: string;
  eventName: string;
  username: string;
}

interface ApiErrorShape {
  message?: string;
  response?: {
    data?: {
      message?: string;
    };
  };
}

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;

const getShareFighterName = (fighter: ShareFighter): string => {
  const fullName = [fighter.firstName, fighter.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || "Hoowlr fighter";
};

const getShareFighterMeta = (fighter: ShareFighter): string => {
  const location =
    [fighter.city, fighter.country].filter(Boolean).join(", ") ||
    fighter.location ||
    "";

  return [fighter.trainingDiscipline, fighter.weightClass, location]
    .filter(Boolean)
    .join(" • ");
};

const normalizeEmail = (value: string): string => value.trim().toLowerCase();

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }

      resolve(reader.result.split(",")[1] || "");
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== "object") return fallback;

  const apiError = error as ApiErrorShape;
  return apiError.response?.data?.message || apiError.message || fallback;
};

const EventShareIcon: React.FC<{ className?: string; }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M21 11.2 13.8 4v4.05C7.72 8.64 4.2 12.3 3 18.9c2.18-3.15 5.42-4.85 10.8-4.85V18L21 11.2Z" />
  </svg>
);

const EventShare: React.FC<EventShareProps> = ({ eventId, eventName, username }) => {
  const { t } = useTranslation("events");

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSharingEvent, setIsSharingEvent] = useState(false);
  const [activeShareTab, setActiveShareTab] = useState<ShareTab>("fighters");
  const [emailInput, setEmailInput] = useState("");
  const [shareEmails, setShareEmails] = useState<string[]>([]);
  const [shareFighters, setShareFighters] = useState<ShareFighter[]>([]);
  const [selectedShareFighters, setSelectedShareFighters] =
    useState<ShareFighter[]>([]);
  const [fighterSearch, setFighterSearch] = useState("");
  const [fighterPage, setFighterPage] = useState(0);
  const [fighterTotalPages, setFighterTotalPages] = useState(1);
  const [fighterTotal, setFighterTotal] = useState(0);
  const [fightersLoading, setFightersLoading] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [shareMessage, setShareMessage] = useState("");

  const resetShareModalState = () => {
    setEmailInput("");
    setShareEmails([]);
    setShareFighters([]);
    setSelectedShareFighters([]);
    setFighterSearch("");
    setActiveShareTab("fighters");
    setFighterPage(0);
    setFighterTotalPages(1);
    setFighterTotal(0);
    setAttachmentFiles([]);
    setShareMessage("");
  };

  const handleOpenShareModal = () => {
    resetShareModalState();
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    if (isSharingEvent) return;

    setIsShareModalOpen(false);
    resetShareModalState();
  };

  useEffect(() => {
    if (!isShareModalOpen) return;

    let isActive = true;

    const timeoutId = window.setTimeout(async () => {
      setFightersLoading(true);

      try {
        const result = await getFighters({
          name: fighterSearch.trim() || undefined,
          page: fighterPage,
          pageSize: 8,
        });

        if (!isActive) return;

        setShareFighters(
          Array.isArray(result?.data) ? result.data : [],
        );
        setFighterTotal(typeof result?.total === "number" ? result.total : 0);
        setFighterTotalPages(
          typeof result?.totalPages === "number" && result.totalPages > 0
            ? result.totalPages
            : 1,
        );
      } catch (error) {
        if (!isActive) return;

        console.error("Failed to load fighters for event sharing:", error);
        setShareFighters([]);
        setFighterTotal(0);
        setFighterTotalPages(1);
      } finally {
        if (isActive) setFightersLoading(false);
      }
    }, 300);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [isShareModalOpen, fighterSearch, fighterPage]);

  const toggleShareFighter = (fighter: ShareFighter) => {
    if (!fighter.id || isSharingEvent) return;

    setSelectedShareFighters((current) => {
      const alreadySelected = current.some(
        (item) => item.id === fighter.id,
      );

      if (alreadySelected) {
        return current.filter((item) => item.id !== fighter.id);
      }

      return [...current, fighter];
    });
  };

  const removeSelectedShareFighter = (fighterId: string) => {
    if (isSharingEvent) return;

    setSelectedShareFighters((current) =>
      current.filter((fighter) => fighter.id !== fighterId),
    );
  };

  const handleAddEmail = () => {
    if (isSharingEvent) return;

    const normalizedEmail = normalizeEmail(emailInput);
    if (!normalizedEmail) return;

    if (!isValidEmail(normalizedEmail)) {
      toast.error(
        t("share_event_invalid_email", {
          email: normalizedEmail,
          defaultValue: "Invalid email: {{email}}",
        }),
      );
      return;
    }

    const alreadyAdded = shareEmails.some(
      (email) => email.toLowerCase() === normalizedEmail,
    );

    if (alreadyAdded) {
      toast.error(
        t(
          "share_event_email_already_added",
          "This email address has already been added.",
        ),
      );
      return;
    }

    setShareEmails((current) => [...current, normalizedEmail]);
    setEmailInput("");
  };

  const handleEmailKeyDown = (
    keyboardEvent: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (keyboardEvent.key !== "Enter") return;

    keyboardEvent.preventDefault();
    handleAddEmail();
  };

  const removeShareEmail = (emailToRemove: string) => {
    if (isSharingEvent) return;

    setShareEmails((current) =>
      current.filter((email) => email !== emailToRemove),
    );
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles: File[] = [];

    selectedFiles.forEach((file) => {
      if (file.size > MAX_ATTACHMENT_SIZE) {
        toast.error(
          t(
            "attachment_too_large",
            {
              fileName: file.name,
              defaultValue: "{{fileName}} is too large. Max size is 25MB.",
            },
          ),
        );
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setAttachmentFiles((current) => [...current, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    if (isSharingEvent) return;

    setAttachmentFiles((current) =>
      current.filter((_, fileIndex) => fileIndex !== index),
    );
  };

  const uploadAttachments = async (): Promise<ShareEventFileRequest[]> => {
    if (attachmentFiles.length === 0) return [];

    setIsUploadingAttachments(true);

    try {
      return await Promise.all(
        attachmentFiles.map(async (file) => {
          const [fileUrl, fileBase64] = await Promise.all([
            uploadMediaFile(file),
            fileToBase64(file),
          ]);

          return {
            fileName: file.name,
            fileUrl,
            fileType: file.type,
            fileSize: file.size,
            fileBase64,
          };
        }),
      );
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const handleShareEvent = async () => {
    if (!eventId || isSharingEvent) return;

    const hoowlrProfileIds = Array.from(
      new Set(
        selectedShareFighters
          .map((fighter) => fighter.id.trim())
          .filter(Boolean),
      ),
    );

    const nonHoowlrEmails = [...shareEmails];
    const pendingEmail = normalizeEmail(emailInput);

    if (pendingEmail) {
      if (!isValidEmail(pendingEmail)) {
        toast.error(
          t("share_event_invalid_email", {
            email: pendingEmail,
            defaultValue: "Invalid email: {{email}}",
          }),
        );
        return;
      }

      const alreadyAdded = nonHoowlrEmails.some(
        (email) => email.toLowerCase() === pendingEmail,
      );

      if (!alreadyAdded) nonHoowlrEmails.push(pendingEmail);
    }

    if (hoowlrProfileIds.length === 0 && nonHoowlrEmails.length === 0) {
      toast.error(
        t(
          "share_event_recipient_required",
          "Select at least one Hoowlr fighter or add an email address.",
        ),
      );
      return;
    }

    setIsSharingEvent(true);

    try {
      let attachments: ShareEventFileRequest[];

      try {
        attachments = await uploadAttachments();
      } catch (error) {
        toast.error(
          getErrorMessage(
            error,
            t("attachment_upload_failed", "Failed to upload attachment(s)"),
          ),
        );
        return;
      }

      const request: ShareEventRequest = {
        hoowlrProfileIds,
        nonHoowlrEmails,
        message: shareMessage.trim() || undefined,
        attachments,
      };

      const response = await shareEvent(username, eventId, request);

      toast.success(
        response?.message ||
        t("event_shared_successfully", "Event shared successfully"),
      );

      setIsShareModalOpen(false);
      resetShareModalState();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t("failed_to_share_event", "Failed to share event"),
        ),
      );
    } finally {
      setIsSharingEvent(false);
    }
  };

  return (
    <>
      <Tooltip content={t("share_event", "Share event")} placement="bottom">
        <Button
          isIconOnly
          size="sm"
          variant="light"
          onPress={handleOpenShareModal}
          aria-label={t("share_event", "Share event")}
          className="h-9 w-9 min-w-9 shrink-0 rounded-lg text-black hover:bg-gray-100"
        >
          <EventShareIcon className="h-7 w-7" />
        </Button>
      </Tooltip>
      <Modal
        isOpen={isShareModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseShareModal();
          }
        }}
        size="4xl"
        backdrop="opaque"
        scrollBehavior="inside"
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        classNames={{
          backdrop: "bg-black/40",
          base: "w-[calc(100vw-2rem)] max-w-[900px] max-h-[88dvh] overflow-hidden rounded-3xl bg-white shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08)]",
          header: "border-b border-gray-100 px-8 pt-6 pb-4",
          body: "px-8 py-6",
          footer: "border-t border-gray-100 px-8 pb-6 pt-4",
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-800">
                      <EventShareIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-xl font-bold text-gray-900">
                        {t("share_event", "Share event")}
                      </h3>
                      <p className="mt-0.5 truncate text-sm font-normal text-gray-500">
                        {eventName}
                      </p>
                    </div>
                  </div>
                </div>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setActiveShareTab("fighters")}
                      disabled={isSharingEvent}
                      className={`flex min-h-[52px] items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-70 ${activeShareTab === "fighters"
                          ? "border-blue-500 bg-blue-50/40 text-blue-600 shadow-[0_0_0_1px_rgba(59,130,246,0.25)]"
                          : "border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${activeShareTab === "fighters"
                            ? "bg-blue-50 text-blue-600"
                            : "bg-gray-100 text-gray-500"
                          }`}
                      >
                        <UserGroupIcon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold">
                          {t("invite_fighters", "Invite fighters")}
                        </span>
                        <span
                          className={`mt-0.5 block truncate text-xs ${activeShareTab === "fighters"
                              ? "text-blue-500/80"
                              : "text-gray-400"
                            }`}
                        >
                          {t(
                            "invite_fighters_help",
                            "Select from Hoowlr fighters",
                          )}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveShareTab("email")}
                      disabled={isSharingEvent}
                      className={`flex min-h-[52px] items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-70 ${activeShareTab === "email"
                          ? "border-blue-500 bg-blue-50/40 text-blue-600 shadow-[0_0_0_1px_rgba(59,130,246,0.25)]"
                          : "border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${activeShareTab === "email"
                            ? "bg-blue-50 text-blue-600"
                            : "bg-gray-100 text-gray-500"
                          }`}
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold">
                          {t("invite_by_email", "Invite by email")}
                        </span>
                        <span
                          className={`mt-0.5 block truncate text-xs ${activeShareTab === "email"
                              ? "text-blue-500/80"
                              : "text-gray-400"
                            }`}
                        >
                          {t("invite_by_email_help", "Send via email address")}
                        </span>
                      </span>
                    </button>
                  </div>
                  {activeShareTab === "fighters" ? (
                    <div>
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {t(
                              "select_hoowlr_fighters",
                              "Select Hoowlr fighters",
                            )}
                          </p>
                          <p className="mt-0.5 text-[11px] leading-4 text-gray-400">
                            {t(
                              "select_hoowlr_fighters_help",
                              "Choose people from the list.",
                            )}
                          </p>
                        </div>
                        {selectedShareFighters.length > 0 && (
                          <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600">
                            {selectedShareFighters.length}{" "}
                            {t("selected", "selected")}
                          </span>
                        )}
                      </div>
                      <Input
                        value={fighterSearch}
                        onValueChange={(value) => {
                          setFighterSearch(value);
                          setFighterPage(0);
                        }}
                        placeholder={t(
                          "search_fighters_to_share",
                          "Search fighters by name",
                        )}
                        size="sm"
                        isClearable
                        isDisabled={isSharingEvent}
                        startContent={
                          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                        }
                        classNames={{
                          input: "text-sm",
                          inputWrapper:
                            "h-10 border border-gray-200 bg-gray-50 shadow-none hover:bg-gray-50 data-[focus=true]:border-blue-400 data-[focus=true]:bg-white",
                        }}
                      />
                      {selectedShareFighters.length > 0 && (
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          {selectedShareFighters.map((fighter) => {
                            const fighterMeta = getShareFighterMeta(fighter);
                            return (
                              <div
                                key={`selected-${fighter.id}`}
                                className="flex min-w-0 items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-2.5"
                              >
                                <Avatar
                                  src={fighter.profileImage || undefined}
                                  name={getShareFighterName(fighter)}
                                  className="h-8 w-8 shrink-0 bg-white text-gray-700 ring-1 ring-blue-100"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    <p className="truncate text-sm font-semibold text-gray-800">
                                      {getShareFighterName(fighter)}
                                    </p>
                                    {fighter.verified && (
                                      <CheckBadgeIcon className="h-3.5 w-3.5 shrink-0 text-gray-500" />
                                    )}
                                  </div>
                                  <p className="mt-0.5 truncate text-[10px] text-gray-400">
                                    {fighterMeta ||
                                      t("selected_fighter", "Selected fighter")}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeSelectedShareFighter(fighter.id)
                                  }
                                  disabled={isSharingEvent}
                                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-blue-500 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  aria-label={t(
                                    "remove_selected_fighter",
                                    "Remove selected fighter",
                                  )}
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white">
                        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/70 px-3 py-2">
                          <span className="text-xs font-medium text-gray-500">
                            {fightersLoading
                              ? t("loading_fighters", "Loading fighters...")
                              : t("fighters_found", {
                                count: fighterTotal,
                                defaultValue: "{{count}} fighters",
                              })}
                          </span>
                          {!fightersLoading && fighterTotalPages > 1 && (
                            <span className="text-[10px] text-gray-400">
                              {t("page", "Page")} {fighterPage + 1} /{" "}
                              {fighterTotalPages}
                            </span>
                          )}
                        </div>
                        <div>
                          {fightersLoading ? (
                            <div className="flex min-h-[180px] items-center justify-center">
                              <Spinner size="sm" color="primary" />
                            </div>
                          ) : shareFighters.length === 0 ? (
                            <div className="flex min-h-[180px] flex-col items-center justify-center px-6 text-center">
                              <MagnifyingGlassIcon className="h-6 w-6 text-gray-300" />
                              <p className="mt-2 text-xs font-medium text-gray-500">
                                {t("no_fighters_found", "No fighters found")}
                              </p>
                              <p className="mt-1 text-[10px] text-gray-400">
                                {fighterSearch
                                  ? t(
                                    "try_different_fighter_search",
                                    "Try a different name.",
                                  )
                                  : t(
                                    "fighters_not_available",
                                    "No fighters are currently available.",
                                  )}
                              </p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-100">
                              {shareFighters.map((fighter) => {
                                const selected = selectedShareFighters.some(
                                  (item) => item.id === fighter.id,
                                );
                                const fighterMeta =
                                  getShareFighterMeta(fighter);
                                return (
                                  <button
                                    key={fighter.id}
                                    type="button"
                                    disabled={isSharingEvent}
                                    onClick={() => toggleShareFighter(fighter)}
                                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${
                                      selected
                                        ? "bg-blue-50 ring-1 ring-inset ring-blue-200"
                                        : "bg-white hover:bg-gray-50"
                                    }`}
                                  >
                                    <Avatar
                                      src={fighter.profileImage || undefined}
                                      name={getShareFighterName(fighter)}
                                      size="sm"
                                      className="shrink-0 bg-gray-100 text-gray-700"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-1.5">
                                        <span className="truncate text-sm font-semibold text-gray-800">
                                          {getShareFighterName(fighter)}
                                        </span>
                                        {fighter.verified && (
                                          <CheckBadgeIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                        )}
                                      </div>
                                      <p className="mt-0.5 truncate text-[10px] text-gray-400">
                                        {fighterMeta ||
                                          t("hoowlr_fighter", "Hoowlr fighter")}
                                      </p>
                                    </div>
                                    <div
                                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${selected
                                          ? "border-blue-600 bg-blue-600 text-white"
                                          : "border-gray-300 bg-white text-transparent"
                                        }`}
                                    >
                                      <CheckIcon className="h-3.5 w-3.5" />
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {!fightersLoading && fighterTotalPages > 1 && (
                          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-3 py-2">
                            <Button
                              size="sm"
                              variant="light"
                              isDisabled={fighterPage <= 0 || isSharingEvent}
                              onPress={() =>
                                setFighterPage((page) => Math.max(0, page - 1))
                              }
                              className="h-8 min-w-20 text-xs text-gray-600"
                            >
                              {t("previous", "Previous")}
                            </Button>
                            <span className="text-[10px] font-medium text-gray-400">
                              {fighterPage + 1} / {fighterTotalPages}
                            </span>
                            <Button
                              size="sm"
                              variant="light"
                              isDisabled={
                                fighterPage >= fighterTotalPages - 1 ||
                                isSharingEvent
                              }
                              onPress={() =>
                                setFighterPage((page) =>
                                  Math.min(fighterTotalPages - 1, page + 1),
                                )
                              }
                              className="h-8 min-w-20 text-xs text-gray-600"
                            >
                              {t("next", "Next")}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                      <div className="flex items-start justify-between gap-3 border-b border-gray-100 bg-gray-50/60 px-4 py-3">
                        <div className="flex min-w-0 items-start gap-2.5">
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600">
                            <EnvelopeIcon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800">
                              {t("share_by_email", "Share by email")}
                            </p>
                            <p className="mt-0.5 text-[11px] leading-4 text-gray-400">
                              {t(
                                "share_event_email_single_help",
                                "Add one email address at a time. Each recipient will appear separately below.",
                              )}
                            </p>
                          </div>
                        </div>
                        {shareEmails.length > 0 && (
                          <span className="shrink-0 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-gray-500">
                            {shareEmails.length} {t("added", "added")}
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input
                            type="email"
                            value={emailInput}
                            onValueChange={setEmailInput}
                            onKeyDown={handleEmailKeyDown}
                            placeholder={t(
                              "enter_email_address",
                              "Enter one email address",
                            )}
                            size="sm"
                            isDisabled={isSharingEvent}
                            startContent={
                              <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                            }
                            className="flex-1"
                            classNames={{
                              input: "text-sm",
                              inputWrapper:
                                "h-10 border border-gray-200 bg-gray-50 shadow-none hover:bg-gray-50 data-[focus=true]:border-blue-400 data-[focus=true]:bg-white",
                            }}
                          />
                          <Button
                            size="sm"
                            color="primary"
                            variant="flat"
                            isDisabled={!emailInput.trim() || isSharingEvent}
                            onPress={handleAddEmail}
                            startContent={<PlusIcon className="h-4 w-4" />}
                            className="h-10 shrink-0 px-4 font-semibold sm:min-w-[110px]"
                          >
                            {t("add_email", "Add email")}
                          </Button>
                        </div>
                        <p className="mt-2 text-[10px] text-gray-400">
                          {t(
                            "share_event_email_enter_hint",
                            "Press Enter or click Add email. Comma-separated addresses are not required.",
                          )}
                        </p>
                        {shareEmails.length > 0 ? (
                          <div className="mt-4 grid gap-2 sm:grid-cols-2">
                            {shareEmails.map((email, index) => (
                              <div
                                key={email}
                                className="flex min-w-0 items-center gap-3 rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2.5"
                              >
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-gray-500 shadow-sm ring-1 ring-gray-200">
                                  <EnvelopeIcon className="h-4 w-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-semibold text-gray-800">
                                    {email}
                                  </p>
                                  <p className="mt-0.5 text-[10px] text-gray-400">
                                    {t("email_recipient", "Email recipient")} #
                                    {index + 1}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeShareEmail(email)}
                                  disabled={isSharingEvent}
                                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                                  aria-label={t(
                                    "remove_email_recipient",
                                    "Remove email recipient",
                                  )}
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-4 flex min-h-16 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/40 px-4 text-center">
                            <p className="text-[11px] text-gray-400">
                              {t(
                                "no_email_recipients_added",
                                "No email recipients added yet.",
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="mb-1.5 text-sm font-semibold text-gray-800">
                      {t("message", "Message")}
                    </p>
                    <Textarea
                      value={shareMessage}
                      onValueChange={setShareMessage}
                      placeholder={t(
                        "share_event_message_placeholder",
                        "Add a message (optional)",
                      )}
                      size="sm"
                      minRows={4}
                      isDisabled={isSharingEvent}
                      classNames={{
                        input: "text-sm",
                        inputWrapper:
                          "min-h-28 border border-gray-200 bg-gray-50 shadow-none hover:bg-gray-50 data-[focus=true]:border-blue-400 data-[focus=true]:bg-white",
                      }}
                    />
                  </div>
                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-800">
                        {t("attachments", "Attachments")}
                      </p>
                      <label
                        className={`flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50 ${isSharingEvent ? "pointer-events-none opacity-50" : ""
                          }`}
                      >
                        <PaperClipIcon className="h-3.5 w-3.5" />
                        {t("add_file", "Add file")}
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileSelect}
                          disabled={isSharingEvent}
                        />
                      </label>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-3">
                      {attachmentFiles.length === 0 ? (
                        <p className="text-[11px] text-gray-400">
                          {t("no_attachments_added", "No files attached yet.")}
                        </p>
                      ) : (
                        <div className="space-y-1.5">
                          {attachmentFiles.map((file, index) => (
                            <div
                              key={`${file.name}-${index}`}
                              className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-2.5 py-1.5"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-xs font-medium text-gray-700">
                                  {file.name}
                                </p>
                                <p className="text-[10px] text-gray-400">
                                  {formatFileSize(file.size)}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                disabled={isSharingEvent}
                                className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                                aria-label={t(
                                  "remove_attachment",
                                  "Remove attachment",
                                )}
                              >
                                <XMarkIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  size="sm"
                  variant="light"
                  isDisabled={isSharingEvent}
                  onPress={handleCloseShareModal}
                  className="rounded-full bg-gray-100 px-5 py-2 font-medium text-gray-600 hover:bg-gray-200"
                >
                  {t("cancel", "Cancel")}
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  isLoading={isSharingEvent}
                  onPress={handleShareEvent}
                  startContent={
                    !isSharingEvent ? (
                      <EventShareIcon className="h-4 w-4" />
                    ) : undefined
                  }
                  className="rounded-full px-5 py-2 font-bold"
                >
                  {isUploadingAttachments
                    ? t("uploading", "Uploading...")
                    : t("share", "Share")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
export default EventShare;